// ── Admin radial menu ──
// Everything about the long-press action wheel for map objects: which options an object
// offers, where they land on screen, which one the pointer is currently over, and the
// marker "pop" that marks the object the wheel belongs to.
//
// The wheel's option centers live in `scene.admin.radialCenters` (client coordinates,
// written by openAdminRadialMenu and hit-tested by detectAdminRadialAction). Extracted
// verbatim from useMapAdminInteraction; the only change is closure variables → an
// explicit deps object.
import type { Dispatch, SetStateAction, MutableRefObject } from 'react';
import * as PIXI from 'pixi.js';
import type { Viewport } from 'pixi-viewport';
import type { MapObject } from '../../../models/mapObject';
import type { MapSceneState } from '../mapSceneState';

// Hit box around each option's center, in client pixels — wide and short because the
// options are stacked text rows, not icons on a circle.
const ADMIN_RADIAL_HIT_WIDTH = 180;
const ADMIN_RADIAL_HIT_HEIGHT = 34;

export interface AdminRadialMenuOption {
  key: string;
  icon: string;
  label: string;
}

export interface AdminRadialMenuDeps {
  sceneRef: MutableRefObject<MapSceneState>;
  getAdminRadialPositions: (count: number) => Array<{ xOffset: number; yOffset: number }>;
  setAdminRadialMenuPosition: Dispatch<SetStateAction<{ x: number; y: number }>>;
  setAdminRadialMenuMapObject: Dispatch<SetStateAction<{ mapObject: MapObject; marker: PIXI.Container } | null>>;
  setShowAdminRadialMenu: Dispatch<SetStateAction<boolean>>;
  setHighlightedAdminAction: Dispatch<SetStateAction<string | null>>;
  /** Snapshot objectModes at open so the React overlay never reads sceneRef during render. */
  setAdminRadialObjectMode: Dispatch<SetStateAction<'dnd' | 'resize'>>;
}

/** Where a wheel is being opened: for which object, on which marker, at which client point. */
export interface AdminRadialMenuTarget {
  mapObject: MapObject;
  marker: PIXI.Container;
  screenX: number;
  screenY: number;
}

/** Pure options list for a known edit mode (no sceneRef — safe during React render). */
export function buildAdminRadialMenuOptions(
  mode: 'dnd' | 'resize',
  objectType?: string,
): AdminRadialMenuOption[] {
  if (objectType?.startsWith('island_')) {
    return [
      { key: 'mirror_h', icon: '↔️', label: 'Mirror' },
      { key: 'rotate_180', icon: '↔️', label: 'Mirror' },
      { key: 'duplicate', icon: '📋', label: 'Duplicate' },
      { key: 'delete', icon: '🗑️', label: 'Delete' },
    ];
  }
  const options: AdminRadialMenuOption[] = [
    {
      key: 'toggle_mode',
      icon: mode === 'dnd' ? '✋' : '↔️',
      label: mode === 'dnd' ? 'Mode: D&D' : 'Mode: Resize',
    },
    {
      key: 'rotate_180',
      icon: '🔄',
      label: 'Flip',
    },
    {
      key: 'delete',
      icon: '🗑️',
      label: 'Delete',
    },
  ];
  if (mode === 'resize') {
    options.push({
      key: 'default_size',
      icon: '📐',
      label: 'Default size',
    });
  }
  options.push({
    key: 'duplicate',
    icon: '📋',
    label: 'Duplicate',
  });
  return options;
}

/** Islands get a fixed set; every other object's set depends on its current edit mode. */
export function getAdminRadialMenuOptions(
  sceneRef: MutableRefObject<MapSceneState>,
  objectId: number,
  objectType?: string,
): AdminRadialMenuOption[] {
  const mode = sceneRef.current.admin.objectModes.get(objectId) ?? 'dnd';
  return buildAdminRadialMenuOptions(mode, objectType);
}

/** Which option is under a client point — nearest center wins when hit boxes overlap. */
export function detectAdminRadialAction(
  sceneRef: MutableRefObject<MapSceneState>,
  screenX: number,
  screenY: number,
): string | null {
  let bestKey: string | null = null;
  let bestDist = Infinity;
  for (const option of sceneRef.current.admin.radialCenters) {
    if (Math.abs(screenX - option.x) <= ADMIN_RADIAL_HIT_WIDTH / 2 &&
        Math.abs(screenY - option.y) <= ADMIN_RADIAL_HIT_HEIGHT / 2) {
      const dist = (screenX - option.x) ** 2 + (screenY - option.y) ** 2;
      if (dist < bestDist) {
        bestDist = dist;
        bestKey = option.key;
      }
    }
  }
  return bestKey;
}

/** Pops the marker the wheel belongs to by 6%, restoring whichever marker was popped before. */
function activateAdminMarkerVisual(
  sceneRef: MutableRefObject<MapSceneState>,
  marker: PIXI.Container,
): void {
  const prev = sceneRef.current.admin.radialMarkerState;
  if (prev?.marker === marker) return;
  if (prev?.marker?.scale) {
    prev.marker.scale.set(prev.scaleX, prev.scaleY);
  }
  sceneRef.current.admin.radialMarkerState = {
    marker,
    scaleX: marker.scale.x,
    scaleY: marker.scale.y,
  };
  marker.scale.set(marker.scale.x * 1.06, marker.scale.y * 1.06);
}

/** Builds the option centers, clamps the wheel into the viewport and shows it. */
export function openAdminRadialMenu(deps: AdminRadialMenuDeps, target: AdminRadialMenuTarget): void {
  const {
    sceneRef,
    getAdminRadialPositions,
    setAdminRadialMenuPosition,
    setAdminRadialMenuMapObject,
    setShowAdminRadialMenu,
    setHighlightedAdminAction,
    setAdminRadialObjectMode,
  } = deps;
  const { mapObject, marker, screenX, screenY } = target;

  const mode = sceneRef.current.admin.objectModes.get(mapObject.id) ?? 'dnd';
  const menuOptions = buildAdminRadialMenuOptions(mode, mapObject.type);
  const positions = getAdminRadialPositions(menuOptions.length);

  // Clamp center to viewport with enough padding for the outermost options
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const yOffsets = positions.map(p => p.yOffset);
  const minYOffset = Math.min(...yOffsets);
  const maxYOffset = Math.max(...yOffsets);
  const safePadding = 20;
  const cx = Math.max(110, Math.min(vw - 110, screenX));
  const cy = Math.max(-minYOffset + safePadding, Math.min(vh - maxYOffset - safePadding, screenY));

  sceneRef.current.admin.radialCenters = menuOptions.map((item, index) => ({
    key: item.key,
    x: cx + positions[index].xOffset,
    y: cy + positions[index].yOffset,
  }));

  setAdminRadialObjectMode(mode);
  setAdminRadialMenuPosition({ x: cx, y: cy });
  setAdminRadialMenuMapObject({ mapObject, marker });
  setShowAdminRadialMenu(true);
  setHighlightedAdminAction(detectAdminRadialAction(sceneRef, screenX, screenY));
  activateAdminMarkerVisual(sceneRef, marker);
}

/**
 * A marker's world position as a CLIENT point (canvas offset included) — the coordinate
 * space the radial wheel and its hit test both use. Both long-press paths (island and
 * map object) opened the wheel with this same five-line conversion.
 */
export function markerClientPoint(
  viewport: Viewport,
  app: PIXI.Application | null,
  container: PIXI.Container,
): { x: number; y: number } {
  const screenPos = viewport.toScreen(container.x, container.y);
  const canvasEl = app?.canvas as HTMLCanvasElement | undefined;
  const canvasRect = canvasEl?.getBoundingClientRect();
  return {
    x: screenPos.x + (canvasRect?.left ?? 0),
    y: screenPos.y + (canvasRect?.top ?? 0),
  };
}
